import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  AutoCompleteMulti,
  DateInput,
  FormContainer,
  FormInput,
  SwitchInput,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { useCallback, useMemo } from "react";
import {
  sendLimitDiariesSchema,
  TSendLimitDiaries,
} from "./SendLimitDiariesSchema";
import { TicketService } from "./TicketService";
import { listAutomations } from "../../../../../../services/ticketautomation.service";
import {
  createSendLimitDiary,
  getSendLimitDiaryById,
  updateSendLimitDiaryById,
} from "../../../../../../services/sendLimitDiary.service";

const translationPath = "page.settings.crm.sendLimitDiaries.";

const SendLimitDiaries = () => {
  const { t } = useTranslation();

  const { sendRequest: save, loading: sLoading } =
    useFetch(createSendLimitDiary);

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateSendLimitDiaryById
  );

  const { sendRequest: get, loading: gLoading } = useFetch(
    getSendLimitDiaryById
  );

  const params = useMemo(() => {
    return {
      AllowWhatsappTemplate: true,
      AutomationCodeListExclude: [19],
      Enable: true,
    };
  }, []);

  const handleSave = useCallback((v: TSendLimitDiaries) => {
    return save({
      ...v,
      ticketServiceId: v.ticketService.id,
      startDate: v.startDate.toISOString(),
      endDate: v.endDate.toISOString(),
      valueLimit: Number(v.valueLimit),
      ticketAutomationsIds: v.ticketAutomations?.map((item) => item.id),
    });
  }, []);

  const updateHandler = useCallback((v: TSendLimitDiaries, id: string) => {
    return update({
      item: {
        ...v,
        id: id,
        ticketServiceId: v.ticketService.id,
        startDate: v.startDate.toISOString(),
        endDate: v.endDate.toISOString(),
        valueLimit: Number(v.valueLimit),
        ticketAutomationsIds: v.ticketAutomations?.map((item) => item.id),
      },
      id: id,
    });
  }, []);

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TSendLimitDiaries>> => {
      const { data, success } = await get(id);

      if (data) {
        const newData: TSendLimitDiaries = {
          ...data,
          startDate: new Date(data.startDate),
          endDate: new Date(data.endDate),
          valueLimit: data.valueLimit.toString(),
        };
        return { data: newData, success: true };
      }
      return { data, success };
    },
    [get]
  );

  const loading = sLoading || uLoading || gLoading;

  return (
    <FormContainer<TSendLimitDiaries>
      saveFunction={handleSave}
      updateFunction={updateHandler}
      getFunction={getHandler}
      loading={loading}
      title={t(translationPath + "send_limit_diaries")}
      schema={sendLimitDiariesSchema}
      subtitle={t(translationPath + "new_send_limit_diaries")}
      subtitleWatchField="name"
      fields={() => (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} textAlign="end">
            <SwitchInput name="enable" label={t(translationPath + "enable")} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormInput
              size="small"
              name="name"
              fullWidth
              label={t(translationPath + "name")}
            />
          </Grid>

          <TicketService />

          <Grid item xs={12} sm={6}>
            <DateInput
              name="startDate"
              label={t(translationPath + "start_date")}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DateInput name="endDate" label={t(translationPath + "end_date")} />
          </Grid>

          <Grid item xs={12} sm={12}>
            <AutoCompleteMulti
              name="ticketAutomations"
              request={listAutomations}
              params={params}
              label={t(translationPath + "automation")}
              getOptionLabel={(option) => option.name}
              size="small"
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormInput
              size="small"
              name="valueLimit"
              type="number"
              fullWidth
              label={t(translationPath + "quantity")}
            />
          </Grid>

          <Grid item xs={12} sm={8}>
            <FormInput
              size="small"
              name="alertMail"
              fullWidth
              label={t(translationPath + "alert_mail")}
            />
          </Grid>

          <Grid item xs={12} sm={12}>
            <FormInput
              size="small"
              name="description"
              fullWidth
              multiline
              minRows={4}
              label={t(translationPath + "description")}
            />
          </Grid>
        </Grid>
      )}
    />
  );
};

export default SendLimitDiaries;
